import { createSelector } from 'reselect';
import { pageContext, get } from 'ctm-util-functions';

import {
    experimentIdSelector,
    invalidElementsSelector,
    pageNameSelector,
    verticalNameSelector
} from '../base';

import {
    selectedProductSelector,
    quoteIdSelector
} from '../results';

import { quoteSelector } from '../form_payload';
import {
    getCarJourney, getHomeContentsJourney,
    getCarEcommerce, getHomeContentsEcommerce
} from './verticals';
import pageNames from './verticals/page_names';

/**
 * Creates a reselect selector for validation error tracking .
 */
export const errorTrackingSelector = createSelector(
    [
        invalidElementsSelector
    ],
    (invalidElements) => {
        // collect the first 5 elements with errors
        const errors = invalidElements.slice(0, 5).toJS();
        return errors.map((error) => {
            const newError = error;
            newError.validationMessage = newError.invalidRules[0].validationMessage;
            delete newError.invalidRules;
            return newError;
        });
    }
);

const payloadMap = {
    car: {
        journey: getCarJourney,
        ecommerce: getCarEcommerce
    },
    home_contents: {
        journey: getHomeContentsJourney,
        ecommerce: getHomeContentsEcommerce
    }
};

const getJourneyPayload = (state) => {
    const verticalName = verticalNameSelector(state);
    const quote = quoteSelector(state);
    const verticalTrackingFormPayload = pageContext.getProps().verticalTrackingFormPayload;
    const quotePayload = verticalTrackingFormPayload
        ? verticalTrackingFormPayload({ formPayload: quote, state })
        : quote;

    return payloadMap[verticalName].journey(quotePayload, state);
};

const getEcommercePayload = (type) => (state) => {
    const verticalName = verticalNameSelector(state);
    const pageName = pageNameSelector(state);
    return payloadMap[verticalName].ecommerce(type, pageName, state);
};

const getPage = (vertical, name) => get(pageNames, `${vertical}.${name}`, name).replace(/_/g, ' ');

const getActionStep = (state) => {
    const verticalName = verticalNameSelector(state);
    const pageName = pageNameSelector(state);
    const page = getPage(verticalName, pageName.toLowerCase());
    return pageName.startsWith(verticalName) ? page : `${verticalName} ${page}`;
};

/**
 * Creates a reselect selector for the Form Data from the state.
 * @returns {Object}         - The form data.
 */
export const trackQuoteFormDataSelector = createSelector(
    [
        getActionStep,
        experimentIdSelector,
        getJourneyPayload,
        selectedProductSelector,
        quoteIdSelector
    ],
    (actionStep, experimentId, journeyPayload, selectedProduct, quoteId) => {
        const verticalTrackingSelectors = get(pageContext.getProps(), 'verticalSelectors.tracking');
        const verticalTrackQuoteFormEcommerce = verticalTrackingSelectors ? verticalTrackingSelectors.trackQuoteForm(selectedProduct, quoteId, actionStep) : {};
        const journey = journeyPayload.toJSON();
        return {
            actionStep,
            experimentId,
            journey,
            ...verticalTrackQuoteFormEcommerce
        };
    }
);

/**
 * Creates a reselect selector for ecommerce from the state.
 * @param {string} type - Action type
 * @returns {Object}    - The form data.
 */
export const ecommerceDataSelector = (type) => createSelector(
    [getEcommercePayload(type)],
    (ecommercePayload) => ({ ...ecommercePayload })
);
